import {
    getNotificationEmails,
    updateNotificationEmails,
    type BrandWideNotification,
} from '@api/organization';
import { listVendorsForPicker } from '@api/work-order';
import {
    Button,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    Switch,
    TagsInput,
    Text,
    Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FC, type ReactNode } from 'react';

import VendorRecipientOverrides, { type VendorOverrides } from './VendorRecipientOverrides';

/**
 * One brand-wide notification: whether it sends, and to whom.
 *
 * EXTRACTED ONCE THERE WERE THREE. It was inline in the Email Settings tab while the
 * acknowledgement was the only event of this shape; the two QC lists are the moment a third
 * copy would have appeared. The same reasoning that made one OrderEmailSection serve both
 * order emails.
 *
 * BRAND-WIDE, which is what separates these from the order emails. Those go to one counterparty
 * at a time and are keyed per vendor; these are one announcement and one list. Different
 * questions, so a different component rather than a flag on that one.
 *
 * `enabled` is deliberately separate from an empty To: a brand pausing a notification for a
 * week should not have to cut and re-paste its recipient list to do it. Either being off means
 * nothing sends.
 */
const NotificationCard: FC<{
    event: BrandWideNotification;
    title: string;
    description: ReactNode;
    /** What the switch says, when "Send this email" is too vague among several cards. */
    switchLabel?: string;
    ccDescription?: string;
    /** Rendered inside the card, below the fields — for anything specific to one event. */
    footer?: ReactNode;
    editable: boolean;
    /**
     * Offer per-manufacturer overrides under the brand-wide list.
     *
     * Only for an event whose sender knows which site a message is about; the server ignores
     * the book otherwise, so a card that showed it for the acknowledgement would be lying.
     */
    perManufacturer?: boolean;
}> = ({
    event,
    title,
    description,
    switchLabel,
    ccDescription,
    footer,
    editable,
    perManufacturer,
}) => {
    const query = useQuery({
        queryKey: ['notification-emails', event],
        queryFn: () => getNotificationEmails(event),
    });

    const manufacturers = useQuery({
        queryKey: ['notification-manufacturers'],
        queryFn: listVendorsForPicker,
        enabled: !!perManufacturer,
    });

    const [enabled, setEnabled] = useState(false);
    const [to, setTo] = useState<string[]>([]);
    const [cc, setCc] = useState<string[]>([]);
    const [byVendor, setByVendor] = useState<VendorOverrides>({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /*
     * Seeded from the server once it answers. The form is the draft and the query is the truth;
     * re-seeding on every render would fight the person typing.
     */
    useEffect(() => {
        if (!query.data) return;
        setEnabled(query.data.enabled);
        setTo(query.data.to);
        setCc(query.data.cc);
        setByVendor(query.data.byVendor ?? {});
    }, [query.data]);

    const touch = () => {
        setSaved(false);
        setError(null);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await updateNotificationEmails(event, {
                enabled,
                to,
                cc,
                ...(perManufacturer ? { byVendor } : {}),
            });
            setSaved(true);
            void query.refetch();
        } catch (err: any) {
            setError(
                err?.error?.data?.message
                    ?? err?.error?.message
                    ?? err?.message
                    ?? 'Could not save. Try again.',
            );
        } finally {
            setSaving(false);
        }
    };

    if (query.isLoading || (perManufacturer && manufacturers.isLoading)) {
        return <Loader size="sm" />;
    }

    return (
        <Paper p="md" radius="md" withBorder>
            <Stack gap="sm">
                <div>
                    <Title order={5}>{title}</Title>
                    <Text size="sm" c="dimmed">
                        {description}
                    </Text>
                </div>

                <Switch
                    checked={enabled}
                    disabled={!editable}
                    onChange={(e) => {
                        touch();
                        setEnabled(e.currentTarget.checked);
                    }}
                    label={switchLabel ?? 'Send this email'}
                    description="Nothing sends while this is off, or while nobody is listed below."
                />

                <TagsInput
                    label="To"
                    description="Type an address and press Enter."
                    placeholder="name@company.com"
                    value={to}
                    disabled={!editable}
                    onChange={(v) => {
                        touch();
                        setTo(v);
                    }}
                />

                <TagsInput
                    label="CC"
                    description={ccDescription ?? 'Anyone else who should see it.'}
                    placeholder="name@company.com"
                    value={cc}
                    disabled={!editable}
                    onChange={(v) => {
                        touch();
                        setCc(v);
                    }}
                />

                {enabled && to.length === 0 && (
                    <Text size="xs" c="orange.7">
                        Nobody is listed, so nothing will send.
                    </Text>
                )}

                {perManufacturer && (
                    <>
                        <Divider />
                        <VendorRecipientOverrides
                            vendors={manufacturers.data}
                            byVendor={byVendor}
                            onChange={(next) => {
                                touch();
                                setByVendor(next);
                            }}
                            editable={editable}
                            party="manufacturer"
                            hint="For a site whose deliveries a different desk watches. Leave a site alone and it uses the lists above."
                            toDescription="Replaces the To above for this site's deliveries. Leave blank to keep the default."
                            ccDescription="Copied on this site's deliveries only, in addition to the CC above."
                        />
                    </>
                )}

                {footer}

                {error && (
                    <Text size="xs" c="red.7">
                        {error}
                    </Text>
                )}

                {editable ? (
                    <Group justify="flex-end">
                        <Button size="xs" loading={saving} onClick={() => void save()}>
                            {saved ? 'Saved' : 'Save'}
                        </Button>
                    </Group>
                ) : (
                    <Text size="xs" c="dimmed">
                        Only admins can change who receives these emails.
                    </Text>
                )}
            </Stack>
        </Paper>
    );
};

export default NotificationCard;
